import React, { useState, useMemo } from 'react';
import { 
  X, Calendar, Clock, RefreshCw, Plus, AlertCircle, 
  Check, ChevronDown, ChevronUp, Repeat, DollarSign,
  MapPin, User, Info
} from 'lucide-react';
import { format, addDays, addWeeks, addMonths, parseISO, getDay } from 'date-fns';
import './RecurringJobModal.css';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun', fullLabel: 'Sunday' },
  { value: 1, label: 'Mon', fullLabel: 'Monday' },
  { value: 2, label: 'Tue', fullLabel: 'Tuesday' },
  { value: 3, label: 'Wed', fullLabel: 'Wednesday' },
  { value: 4, label: 'Thu', fullLabel: 'Thursday' },
  { value: 5, label: 'Fri', fullLabel: 'Friday' },
  { value: 6, label: 'Sat', fullLabel: 'Saturday' },
];

const FREQUENCY_OPTIONS = [
  { value: 'one_time', label: 'One-Time', description: 'Single appointment' },
  { value: 'weekly', label: 'Weekly', description: 'Every week' },
  { value: 'biweekly', label: 'Bi-Weekly', description: 'Every 2 weeks' },
  { value: 'every_3_weeks', label: 'Every 3 Weeks', description: 'Every 3 weeks' },
  { value: 'every_4_weeks', label: 'Every 4 Weeks', description: 'Every 4 weeks (not monthly)' },
  { value: 'monthly', label: 'Monthly', description: 'Same day each month' },
  { value: 'twice_weekly', label: '2x Per Week', description: 'Two days per week' },
];

const DURATION_OPTIONS = [
  { value: '4_weeks', label: '4 weeks', weeks: 4 },
  { value: '8_weeks', label: '8 weeks', weeks: 8 },
  { value: '12_weeks', label: '12 weeks (3 months)', weeks: 12 },
  { value: '26_weeks', label: '26 weeks (6 months)', weeks: 26 },
  { value: '52_weeks', label: '52 weeks (1 year)', weeks: 52 },
  { value: 'end_date', label: 'Until specific date', weeks: null },
  { value: 'count', label: 'Specific number of appointments', weeks: null },
  { value: 'indefinite', label: 'Indefinitely (until cancelled)', weeks: null },
];

function RecurringJobModal({ 
  isOpen, 
  onClose, 
  onSave,
  existingJob = null,  // If editing an existing job
  initialDate = null,
  clients = [],
  employees = [],
  properties = []  // Add properties prop
}) {
  // Form state
  const [formData, setFormData] = useState({
    clientId: '',
    clientName: '',
    propertyId: '',  // Add propertyId
    address: '',
    city: 'Charlottesville',
    state: 'VA',
    zip: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    scheduledTime: '09:00',
    duration: 2.5,
    serviceType: 'recurring',
    price: 150,
    notes: '',
    team: [],
    teamNames: [],
  });

  // Recurring options state
  const [recurringOptions, setRecurringOptions] = useState({
    frequency: 'one_time',
    selectedDays: [getDay(new Date())],
    durationType: '12_weeks',
    endDate: '',
    appointmentCount: 12,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Reset state when modal opens or existingJob changes
  React.useEffect(() => {
    if (isOpen) {
      if (existingJob) {
        setFormData({
          clientId: existingJob.clientId || '',
          clientName: existingJob.clientName || '',
          address: existingJob.address || '',
          city: existingJob.city || 'Charlottesville',
          state: existingJob.state || 'VA',
          zip: existingJob.zip || '',
          startDate: existingJob.date || initialDate || format(new Date(), 'yyyy-MM-dd'),
          scheduledTime: existingJob.scheduledTime || '09:00',
          duration: existingJob.duration || 2.5,
          serviceType: existingJob.serviceType || 'recurring',
          price: existingJob.price || 150,
          notes: existingJob.notes || '',
          team: existingJob.team || [],
          teamNames: existingJob.teamNames || [],
        });
        setRecurringOptions({
          frequency: existingJob.frequency || 'one_time',
          selectedDays: existingJob.date ? [getDay(parseISO(existingJob.date))] : [getDay(new Date())],
          durationType: '12_weeks',
          endDate: '',
          appointmentCount: 12,
        });
      } else {
        setFormData({
          clientId: '',
          clientName: '',
          address: '',
          city: 'Charlottesville',
          state: 'VA',
          zip: '',
          startDate: initialDate || format(new Date(), 'yyyy-MM-dd'),
          scheduledTime: '09:00',
          duration: 2.5,
          serviceType: 'recurring',
          price: 150,
          notes: '',
          team: [],
          teamNames: [],
        });
        setRecurringOptions({
          frequency: 'one_time',
          selectedDays: [getDay(new Date())],
          durationType: '12_weeks',
          endDate: '',
          appointmentCount: 12,
        });
      }
      setClientSearch('');
      setShowClientDropdown(false);
    }
  }, [isOpen, existingJob, initialDate]);

  // Filter clients for dropdown
  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients.slice(0, 10);
    const search = clientSearch.toLowerCase();
    return clients.filter(c => 
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(search) ||
      c.phone?.includes(search) ||
      c.email?.toLowerCase().includes(search)
    ).slice(0, 10);
  }, [clients, clientSearch]);

  // Get properties for selected client
  const clientProperties = useMemo(() => {
    if (!formData.clientId) return [];
    return properties.filter(p => p.clientId === formData.clientId);
  }, [properties, formData.clientId]);

  // Select a client from dropdown
  const selectClient = (client) => {
    // Find their properties
    const clientProps = properties.filter(p => p.clientId === client.id);
    const primaryProp = clientProps.find(p => p.isPrimary) || clientProps[0];
    
    setFormData({
      ...formData,
      clientId: client.id,
      clientName: `${client.firstName} ${client.lastName}`.trim(),
      propertyId: primaryProp?.id || '',
      address: primaryProp?.addressLine1 || '',
      city: primaryProp?.city || 'Charlottesville',
      state: primaryProp?.state || 'VA',
      zip: primaryProp?.zip || primaryProp?.zipCode || '',
    });
    setClientSearch('');
    setShowClientDropdown(false);
  };

  // Select a property from dropdown
  const selectProperty = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    if (property) {
      setFormData({
        ...formData,
        propertyId: property.id,
        address: property.addressLine1 || '',
        city: property.city || 'Charlottesville',
        state: property.state || 'VA',
        zip: property.zip || property.zipCode || '',
      });
    }
  };

  // Toggle day selection (for twice weekly)
  const toggleDay = (dayValue) => {
    setRecurringOptions(prev => {
      const days = prev.selectedDays.includes(dayValue)
        ? prev.selectedDays.filter(d => d !== dayValue)
        : [...prev.selectedDays, dayValue].sort((a, b) => a - b);
      return { ...prev, selectedDays: days };
    });
  };

  // Calculate preview of jobs to be created
  const jobPreview = useMemo(() => {
    if (recurringOptions.frequency === 'one_time') {
      return [{
        date: formData.startDate,
        dayName: format(parseISO(formData.startDate), 'EEEE'),
      }];
    }

    const jobs = [];
    let currentDate = parseISO(formData.startDate);
    let maxJobs = 52; // Safety limit
    let endDate = null;
    let targetCount = null;

    // Determine end condition
    const durationOption = DURATION_OPTIONS.find(d => d.value === recurringOptions.durationType);
    if (durationOption?.weeks) {
      endDate = addWeeks(currentDate, durationOption.weeks);
    } else if (recurringOptions.durationType === 'end_date' && recurringOptions.endDate) {
      endDate = parseISO(recurringOptions.endDate);
    } else if (recurringOptions.durationType === 'count') {
      targetCount = recurringOptions.appointmentCount;
      maxJobs = targetCount;
    } else if (recurringOptions.durationType === 'indefinite') {
      // Show preview of first 12 for indefinite
      maxJobs = 12;
    }

    // Generate preview
    let jobCount = 0;
    let iterations = 0;
    const maxIterations = 365; // Safety limit

    // For non-monthly frequencies, find the first matching day
    if (recurringOptions.frequency !== 'monthly') {
      // Find the first date that matches the selected day of week
      const targetDayOfWeek = recurringOptions.selectedDays[0];
      let attempts = 0;
      while (getDay(currentDate) !== targetDayOfWeek && attempts < 7) {
        currentDate = addDays(currentDate, 1);
        attempts++;
      }
    }

    while (jobCount < maxJobs && iterations < maxIterations) {
      iterations++;
      
      if (endDate && currentDate > endDate) break;

      // For monthly, we always add on the same date number
      if (recurringOptions.frequency === 'monthly') {
        jobs.push({
          date: format(currentDate, 'yyyy-MM-dd'),
          dayName: format(currentDate, 'EEEE, MMM d'),
        });
        jobCount++;
        currentDate = addMonths(currentDate, 1);
        continue;
      }

      // Check if current day matches selected days (for twice_weekly)
      const currentDayOfWeek = getDay(currentDate);
      const dayMatches = recurringOptions.frequency === 'twice_weekly'
        ? recurringOptions.selectedDays.includes(currentDayOfWeek)
        : true; // For other frequencies, we've already aligned to the right day

      if (dayMatches) {
        jobs.push({
          date: format(currentDate, 'yyyy-MM-dd'),
          dayName: format(currentDate, 'EEEE, MMM d'),
        });
        jobCount++;

        if (targetCount && jobCount >= targetCount) break;
      }

      // Advance date based on frequency
      if (recurringOptions.frequency === 'weekly') {
        currentDate = addWeeks(currentDate, 1);
      } else if (recurringOptions.frequency === 'biweekly') {
        currentDate = addWeeks(currentDate, 2);
      } else if (recurringOptions.frequency === 'every_3_weeks') {
        currentDate = addWeeks(currentDate, 3);
      } else if (recurringOptions.frequency === 'every_4_weeks') {
        currentDate = addWeeks(currentDate, 4);
      } else if (recurringOptions.frequency === 'twice_weekly') {
        // For twice weekly, advance day by day but only within the week
        currentDate = addDays(currentDate, 1);
        // If we've gone past Saturday, skip to next week's first selected day
        if (getDay(currentDate) === 0) { // Sunday
          const firstSelectedDay = Math.min(...recurringOptions.selectedDays);
          currentDate = addDays(currentDate, firstSelectedDay);
        }
      } else {
        currentDate = addDays(currentDate, 1);
      }
    }

    return jobs;
  }, [formData.startDate, recurringOptions]);

  // Calculate total value
  const totalValue = useMemo(() => {
    return jobPreview.length * (parseFloat(formData.price) || 0);
  }, [jobPreview, formData.price]);

  // Handle save
  const handleSave = () => {
    if (!formData.clientName || !formData.address) {
      alert('Please fill in client name and address');
      return;
    }

    if (recurringOptions.frequency === 'twice_weekly' && recurringOptions.selectedDays.length < 2) {
      alert('Please select at least 2 days for twice weekly service');
      return;
    }

    onSave({
      ...formData,
      frequency: recurringOptions.frequency,
      recurringOptions: recurringOptions.frequency !== 'one_time' ? recurringOptions : null,
      jobsToCreate: jobPreview,
    });
  };

  if (!isOpen) return null;

  const isRecurring = recurringOptions.frequency !== 'one_time';

  return (
    <div className="recurring-modal-wrapper">
      <div className="modal-overlay" onClick={onClose} />
      <div className="recurring-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h2>{existingJob ? 'Edit Job / Make Recurring' : 'Add Job'}</h2>
            <p>{isRecurring ? `${jobPreview.length} appointments will be created` : 'Single appointment'}</p>
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body">
          {/* Client Section */}
          <div className="form-section">
            <h3><User size={18} /> Client Information</h3>
            
            <div className="form-row">
              <div className="form-group client-search-group">
                <label>Client Name *</label>
                <div className="client-search-wrapper">
                  <input
                    type="text"
                    placeholder="Search or enter client name..."
                    value={formData.clientName || clientSearch}
                    onChange={(e) => {
                      if (formData.clientId) {
                        setFormData({ ...formData, clientId: '', clientName: e.target.value });
                      } else {
                        setClientSearch(e.target.value);
                        setFormData({ ...formData, clientName: e.target.value });
                      }
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                  />
                  {showClientDropdown && filteredClients.length > 0 && (
                    <div className="client-dropdown">
                      {filteredClients.map(client => (
                        <button
                          key={client.id}
                          className="client-option"
                          onClick={() => selectClient(client)}
                        >
                          <span className="client-option-name">
                            {client.firstName} {client.lastName}
                          </span>
                          <span className="client-option-phone">{client.phone}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Price per Visit ($)</label>
                <input
                  type="number"
                  step="5"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Job Type</label>
                <select
                  value={formData.serviceType}
                  onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                >
                  <option value="recurring">Recurring</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="deep_clean">Deep Clean</option>
                  <option value="one_time">One-Time</option>
                  <option value="as_needed">As Needed</option>
                  <option value="organizing">Organizing</option>
                  <option value="hoarding">Hoarding</option>
                  <option value="junk_removal">Junk Removal</option>
                  <option value="move_in_out">Move In/Out</option>
                  <option value="commercial">Commercial</option>
                  <option value="laundry_pickup">Laundry Pickup</option>
                </select>
              </div>
            </div>

            {/* Property Dropdown - shows when client is selected */}
            {formData.clientId && clientProperties.length > 0 && (
              <div className="form-group">
                <label>Property *</label>
                <select
                  className="property-select"
                  value={formData.propertyId}
                  onChange={(e) => selectProperty(e.target.value)}
                >
                  <option value="">-- Select Property --</option>
                  {clientProperties.map(prop => (
                    <option key={prop.id} value={prop.id}>
                      {prop.addressLine1}, {prop.city} {prop.state}
                      {prop.sqft ? ` (${prop.sqft.toLocaleString()} sqft)` : ''}
                      {prop.isPrimary ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Manual address entry - shows when no client selected OR no properties */}
            {(!formData.clientId || clientProperties.length === 0) && (
              <>
                <div className="form-group">
                  <label>Address *</label>
                  <input
                    type="text"
                    placeholder="Street address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>

                <div className="form-row three-col">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Zip</label>
                    <input
                      type="text"
                      value={formData.zip}
                      onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Show selected address when property is selected */}
            {formData.clientId && clientProperties.length > 0 && formData.propertyId && (
              <div className="selected-property-info">
                <MapPin size={16} />
                <span>{formData.address}, {formData.city}, {formData.state} {formData.zip}</span>
              </div>
            )}
          </div>

          {/* Schedule Section */}
          <div className="form-section">
            <h3><Calendar size={18} /> Schedule</h3>

            {/* Frequency Selection */}
            <div className="form-group">
              <label>Frequency</label>
              <div className="frequency-grid">
                {FREQUENCY_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={`frequency-option ${recurringOptions.frequency === option.value ? 'selected' : ''}`}
                    onClick={() => setRecurringOptions({ ...recurringOptions, frequency: option.value })}
                  >
                    <span className="freq-label">{option.label}</span>
                    <span className="freq-desc">{option.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Day Selection for twice weekly */}
            {recurringOptions.frequency === 'twice_weekly' && (
              <div className="form-group">
                <label>Select Days (pick 2 or more)</label>
                <div className="days-grid">
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      className={`day-btn ${recurringOptions.selectedDays.includes(day.value) ? 'selected' : ''}`}
                      onClick={() => toggleDay(day.value)}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Day Selection for other frequencies */}
            {isRecurring && recurringOptions.frequency !== 'twice_weekly' && recurringOptions.frequency !== 'monthly' && (
              <div className="form-group">
                <label>Day of Week</label>
                <div className="days-grid">
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      className={`day-btn ${recurringOptions.selectedDays[0] === day.value ? 'selected' : ''}`}
                      onClick={() => setRecurringOptions({ ...recurringOptions, selectedDays: [day.value] })}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>{isRecurring ? 'Start Date' : 'Date'}</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Time</label>
                <input
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Duration</label>
                <select
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseFloat(e.target.value) })}
                >
                  <option value="1">1 hour</option>
                  <option value="1.5">1.5 hours</option>
                  <option value="2">2 hours</option>
                  <option value="2.5">2.5 hours</option>
                  <option value="3">3 hours</option>
                  <option value="3.5">3.5 hours</option>
                  <option value="4">4 hours</option>
                  <option value="5">5 hours</option>
                  <option value="6">6 hours</option>
                </select>
              </div>
            </div>

            {/* Duration Selection for recurring */}
            {isRecurring && (
              <div className="form-group">
                <label>How Long?</label>
                <select
                  value={recurringOptions.durationType}
                  onChange={(e) => setRecurringOptions({ ...recurringOptions, durationType: e.target.value })}
                  className="duration-select"
                >
                  {DURATION_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>

                {recurringOptions.durationType === 'end_date' && (
                  <input
                    type="date"
                    className="end-date-input"
                    value={recurringOptions.endDate}
                    onChange={(e) => setRecurringOptions({ ...recurringOptions, endDate: e.target.value })}
                    min={formData.startDate}
                  />
                )}

                {recurringOptions.durationType === 'count' && (
                  <div className="count-input-wrapper">
                    <input
                      type="number"
                      min="1"
                      max="104"
                      value={recurringOptions.appointmentCount}
                      onChange={(e) => setRecurringOptions({ ...recurringOptions, appointmentCount: parseInt(e.target.value) || 1 })}
                    />
                    <span>appointments</span>
                  </div>
                )}

                {recurringOptions.durationType === 'indefinite' && (
                  <div className="indefinite-note">
                    <Info size={14} />
                    <span>Jobs will be auto-generated as needed. You can cancel anytime.</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="form-section">
            <div className="form-group">
              <label>Notes</label>
              <textarea
                placeholder="Any special instructions..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          {/* Preview Section */}
          {isRecurring && (
            <div className="preview-section">
              <div className="preview-header">
                <h3><Repeat size={18} /> Preview ({jobPreview.length} appointments)</h3>
                <div className="preview-total">
                  <DollarSign size={16} />
                  <span>Total: ${totalValue.toLocaleString()}</span>
                </div>
              </div>
              <div className="preview-list">
                {jobPreview.slice(0, 8).map((job, idx) => (
                  <div key={idx} className="preview-item">
                    <span className="preview-num">{idx + 1}</span>
                    <span className="preview-date">{job.dayName}</span>
                    <span className="preview-time">{formData.scheduledTime}</span>
                    <span className="preview-price">${formData.price}</span>
                  </div>
                ))}
                {jobPreview.length > 8 && (
                  <div className="preview-more">
                    +{jobPreview.length - 8} more appointments...
                  </div>
                )}
              </div>
              {recurringOptions.durationType === 'indefinite' && (
                <div className="preview-note">
                  <Info size={14} />
                  Showing first 12 appointments. More will be auto-generated.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!formData.clientName || !formData.address}
          >
            {isRecurring ? (
              <><RefreshCw size={18} /> Create {jobPreview.length} Jobs</>
            ) : (
              <><Plus size={18} /> Add Job</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RecurringJobModal;
